import type { FC } from 'react';
import { memo, useLayoutEffect, useRef, useState } from 'react';
import type { ITopWin, IWinAmount } from '../../types/topWins.types';
import { splitAmount } from '../../utils/formatAmount';
import styles from './TopWinAmount.module.css';

interface TopWinAmountProps {
  win: ITopWin;
  /** Optional extra class merged onto the wrapper (e.g. for layout tweaks). */
  className?: string;
}

const AmountLine: FC<{ amount: IWinAmount }> = ({ amount }) => {
  const { integer, decimals } = splitAmount(amount.amount);
  const rowRef = useRef<HTMLParagraphElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [scale, setScale] = useState(1);

  // Shrink-to-fit: scale the text down (never up) so a long amount fits its row
  // instead of overflowing/ellipsising. We measure `scrollWidth`, which is
  // layout-based and unaffected by the applied transform — so reading it back
  // never feeds into the scale (no shrink loop, the bug AutoTextfit had here).
  useLayoutEffect(() => {
    const row = rowRef.current;
    const text = textRef.current;
    if (!row || !text) return;

    let raf = 0;
    const fit = () => {
      const natural = text.scrollWidth;
      // Reserve a horizontal breathing margin (--tw-amount-inset px per side) so
      // a scaled-to-fit amount doesn't sit edge-to-edge in the card.
      const inset = parseFloat(getComputedStyle(row).getPropertyValue('--tw-amount-inset')) || 0;
      const available = row.clientWidth - inset * 2;
      // Layout not ready yet (e.g. card mounted while its view was hidden) —
      // retry next frame instead of leaving the amount unscaled.
      if (!natural || available <= 0) {
        raf = requestAnimationFrame(fit);
        return;
      }
      setScale(Math.min(1, available / natural));
    };

    fit();
    // Re-fit when the row resizes (viewport/breakpoint) or the text's intrinsic
    // size changes (font-size swap at a breakpoint). Transform changes don't
    // alter layout size, so observing these can't loop.
    const ro = new ResizeObserver(fit);
    ro.observe(row);
    ro.observe(text);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [integer, decimals, amount.currency]);

  return (
    <p ref={rowRef} className={styles.row}>
      <span
        ref={textRef}
        className={styles.fit}
        style={scale < 1 ? { transform: `scale(${scale})` } : undefined}
      >
        <span className={styles.major}>
          {integer}.{decimals}
        </span>
        <span className={styles.currency}>{amount.currency}</span>
      </span>
    </p>
  );
};

/**
 * Shared Top Wins amount display, used by both the small and featured cards.
 *
 * When the win was converted to the lobby's display currency it shows both
 * values in a vertically-swapping reel; otherwise a single static line. A value
 * too wide for its row is scaled down to fit (never overflows or ellipsises).
 *
 * Size, colour, alignment and vertical nudge are themed by the parent via the
 * `--tw-amount-*` custom properties — see TopWinAmount.module.css.
 */
const TopWinAmount: FC<TopWinAmountProps> = ({ win, className }) => {
  const primary = win.converted ?? win.original;
  // Only when a conversion actually happened do we show the original alongside
  // the converted amount (swapping reel); otherwise a single static line.
  const secondary = win.converted ? win.original : null;

  return (
    <div className={className ? `${styles.wrapper} ${className}` : styles.wrapper}>
      {secondary ? (
        <div className={styles.reel}>
          <AmountLine amount={primary} />
          <AmountLine amount={secondary} />
        </div>
      ) : (
        <AmountLine amount={primary} />
      )}
    </div>
  );
};

export default memo(TopWinAmount);
